import { Component, createRef, FunctionalComponent, h } from 'preact';
import { useRef } from 'preact/hooks';
import * as ReactHintFactory from 'react-hint';
import * as appStyles from '../../assets/global.scss';
import { FinText, KeysOfLocalizations } from '../../i18n';
import { WithStyles, withStyles } from '../../style';
import { stopPropagation } from '../../utils/domUtils';
import Icon from '../Icon/Icon';
import { Key, Properties } from '../types';

const ReactHint = ReactHintFactory({ createElement: h, Component, createRef });

export interface TooltipProps extends Properties {
  iconName?: string;
  placement: string;
  description: KeysOfLocalizations;
  url?: string;
  urlText?: KeysOfLocalizations;
}

type TooltipRenderProps = TooltipProps & WithStyles;

const Tooltip: FunctionalComponent<TooltipProps> = withStyles(appStyles)(
  ({ tx, iconName, description, url, urlText, placement, ...props }: TooltipRenderProps) => {
    const reactHintTriggerRef = useRef<any>();
    const reactHintRef = useRef<any>();
    const openTooltip = (e: KeyboardEvent): void => {
      stopPropagation(e);
      if (e.key === Key.ENTER || e.key === Key.SPACE) {
        reactHintRef.current.toggleHint({ target: reactHintTriggerRef.current.base });
      }
    };
    const onClick = (e: MouseEvent) => {
      e.stopPropagation();
      reactHintRef.current.toggleHint({ target: reactHintTriggerRef.current.base });
    };

    return (
      <div {...props}>
        <ReactHint
          persist
          ref={(ref) => (reactHintRef.current = ref)}
          attribute="data-custom"
          events={{ click: true }}
          className={(tx(props.class), 'absolute bw-custom-hint')}
          onRenderContent={() => (
            <div className={`ps-medium shadow bw-custom-hint__content bw-custom-hint__content-${placement}`}>
              <p>
                <FinText id={description} />
              </p>
              <a
                class={tx('underline medium-type link-hover nordic-blue-900-text font-body-1 mr-xsmall-t block')}
                href={url}
                target="_blank"
                rel="noreferrer"
                aria-describedby="faq-link"
                aria-label="faq"
                onClick={(e: MouseEvent) => e.stopPropagation()}
              >
                <FinText id={urlText} />
                <Icon iconName="external-tab" class={tx('is-xsmall')} />
              </a>
            </div>
          )}
        />
        <Icon
          data-custom
          data-custom-at={placement}
          role="tooltip"
          aria-hidden="true"
          tabIndex={1}
          onClick={onClick}
          aria-describedby="multicity-tooltip"
          aria-label="tooltip-info"
          ref={reactHintTriggerRef}
          onKeyUp={openTooltip}
          iconName={iconName}
          class="is-small flex--nogrow"
        />
      </div>
    );
  }
);

export default Tooltip;
